import { Link } from '@tanstack/react-router';
import { MetricCard, Metric } from './MetricCard.jsx';
import Tag from '../ui/Tag.jsx';
import { caseStudiesIndex } from '../../content/caseStudies.js';

/**
 * CaseStudyCard — index card for one published case study.
 *
 * Composes the MetricCard shell (status accent, hover lift, in-view entrance)
 * around a manifest entry. The whole card is clickable via a stretched-link
 * overlay on the title; stack tags and meta stay presentational beneath it.
 * `featured` renders the wide two-column variant and, when the study carries
 * a real client quote, shows it in the second column.
 */

const MAX_STACK_TAGS = 4;

export function readingTimeMins(wordCount) {
  return Math.max(1, Math.round((wordCount || 0) / 220));
}

export function formatMonthYear(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr || '';
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
}

function CardHead({ label }) {
  return (
    <div className="flex items-center gap-4">
      <span className="font-mono text-mono-xs tracking-[0.12em] uppercase text-ink-300">{label}</span>
    </div>
  );
}

function StackTags({ stack }) {
  if (!Array.isArray(stack) || stack.length === 0) return null;
  const shown = stack.slice(0, MAX_STACK_TAGS);
  const extra = stack.length - shown.length;
  return (
    <div className="flex flex-wrap gap-2">
      {shown.map((s) => (
        <Tag key={s}>{s}</Tag>
      ))}
      {extra > 0 && <Tag>+{extra}</Tag>}
    </div>
  );
}

function TitleLink({ entry, className }) {
  return (
    <h3 className={className}>
      <Link
        to="/case-studies/$slug"
        params={{ slug: entry.slug }}
        className={
          'outline-none focus-visible:text-brand-blue-bright ' +
          "after:absolute after:inset-0 after:content-['']"
        }
      >
        {entry.title}
      </Link>
    </h3>
  );
}

function Outcome({ outcome }) {
  if (!outcome) return null;
  return (
    <div className="mt-5 pt-4 border-t border-white/[.06]">
      <span className="font-mono text-mono-xs tracking-[0.12em] uppercase text-ink-300">
        {caseStudiesIndex.outcomeLabel}
      </span>
      <p className="mt-1.5 text-body-sm text-ink-100 m-0">{outcome}</p>
    </div>
  );
}

function MetaRow({ entry }) {
  return (
    <p className="mt-4 font-mono text-mono-xs tracking-[0.08em] uppercase text-ink-300 m-0">
      {formatMonthYear(entry.date)} · {readingTimeMins(entry.wordCount)} min read
    </p>
  );
}

export default function CaseStudyCard({ entry, featured = false, delay = 0 }) {
  const label = entry.client || entry.tag || entry.type;

  if (featured) {
    return (
      <MetricCard featured>
        <div className={entry.quote ? 'grid md:grid-cols-2 gap-8 md:gap-12 md:items-center' : 'max-w-[720px]'}>
          <div>
            <CardHead label={label} />
            {entry.metric && (
              <div className="mt-6">
                <Metric value={entry.metric} unit={entry.metricUnit} label={entry.metricLabel} />
              </div>
            )}
            <TitleLink
              entry={entry}
              className="mt-6 text-display-sm font-display font-semibold text-ink-50"
            />
            <p className="mt-3 text-body-sm text-ink-200 m-0">{entry.blurb || entry.description}</p>
            <Outcome outcome={entry.outcome} />
            <div className="mt-5">
              <StackTags stack={entry.stack} />
            </div>
            <MetaRow entry={entry} />
          </div>

          {entry.quote && (
            <blockquote className="m-0 md:border-l md:border-white/[.1] md:pl-12">
              <p className="text-display-sm font-display font-medium text-ink-50 leading-snug m-0">
                “{entry.quote}”
              </p>
              {entry.quoteAttrib && (
                <cite className="mt-4 block font-mono text-mono-xs uppercase tracking-[0.12em] text-ink-300 not-italic">
                  {entry.quoteAttrib}
                </cite>
              )}
            </blockquote>
          )}
        </div>
      </MetricCard>
    );
  }

  return (
    <MetricCard delay={delay} className="flex flex-col">
      <CardHead label={label} />
      {entry.metric && (
        <div className="mt-6">
          <Metric value={entry.metric} unit={entry.metricUnit} label={entry.metricLabel} />
        </div>
      )}
      <TitleLink
        entry={entry}
        className={
          (entry.metric ? 'mt-6 text-card-title' : 'mt-6 text-display-sm') +
          ' font-display font-semibold text-ink-50'
        }
      />
      <p className="mt-3 text-body-sm text-ink-200 m-0">{entry.blurb || entry.description}</p>
      <div className="flex-grow" />
      <Outcome outcome={entry.outcome} />
      <div className="mt-5">
        <StackTags stack={entry.stack} />
      </div>
      <MetaRow entry={entry} />
    </MetricCard>
  );
}
